/**
 * Data table for reports.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CollapseReplaceHiddenContext } from '@/components/common/CollapseReplaceWrapper';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { ReportRow } from './ReportRow';
import { ReportCard } from './ReportCard';
import { ReportFilters } from './ReportFilters';
import {
  buildRemoteReportListParams,
  hasRemoteReportActiveFilters,
  isAllReportsTab,
  reportTableExpandedColSpan,
  reportTableHeaders,
  toggleListItem,
} from './report-table-utils';
import { ValidateModal } from '@/components/common/modals/ValidateModal';
import { useDataTable } from '@/hooks/useDataTable';
import { useCurrentSchoolYearId } from '@/hooks/useCurrentSchoolYearId';
import { exportReports, listReports, validateReport } from '@/services/remote/reports.service';
import { useGetReport } from '@/hooks/useReportsRemote';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { ReportDetails } from './renderReportDetails';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { cn } from '@/lib/utils';
import {
  type PerTypeReportPageActions,
  type ReportRemoteFilters as Filters,
  DEFAULT_REPORT_REMOTE_FILTERS as DEFAULT_FILTERS,
  type ReportRemoteFilterGroupId as FilterGroupId,
  ALL_REPORT_REMOTE_FILTER_GROUP_IDS as ALL_FILTER_GROUP_IDS,
  REPORT_REMOTE_FILTER_GROUP_LABELS as FILTER_GROUP_LABELS,
} from '@/components/reports/report-configs';
import type { ReportTab as Tab, ReportDetail, ReportListItem } from '@/types/remote/report.types';
import type { ViewMode, FilterGroup } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';

export type { Filters };

/** Props for {@link ReportsDataTable}. */
interface ReportsDataTableProps {
  activeTab: Tab;
  /** RBAC: tabs the current user is allowed to see (restricts the all-tab query). */
  allowedTabIds?: Tab[];
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditReport?: (id: string) => void;
  onValidate?: (reportId: string) => void;
  enableFilters?: boolean;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  pageActions?: PerTypeReportPageActions;
  initialFilters?: Partial<Filters>;
  renderDetailsContent?: (report: ReportListItem) => React.ReactNode;
  showExport?: boolean;
  isSection?: boolean;
  layoutWidth?: ElementWidth;
  toolbarLeft?: React.ReactNode;
}

/** Remote reports list (table + cards + filters). */
export const ReportsDataTable: React.FC<ReportsDataTableProps> = ({
  activeTab,
  allowedTabIds,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditReport,
  onValidate,
  enableFilters = true,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  pageActions,
  initialFilters,
  renderDetailsContent,
  showExport,
  isSection,
  layoutWidth,
  toolbarLeft,
}) => {
  const queryClient = useQueryClient();

  /** Current school year — used as the default list filter. */
  const currentSchoolYearId = useCurrentSchoolYearId();

  const dt = useDataTable<Filters, FilterGroupId>({
    tableName: 'reports',
    defaultFilters: {
      ...DEFAULT_FILTERS,
      schoolYearId: currentSchoolYearId,
      ...initialFilters,
    },
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidth ?? 'full',
    isSection: isSection ?? false,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const schoolYearInitRef = React.useRef(false);
  /** One-shot: apply current school year once loaded if not already set. */
  React.useEffect(() => {
    if (schoolYearInitRef.current || !currentSchoolYearId) return;
    schoolYearInitRef.current = true;
    dt.primeFilters((prev) => {
      if (prev.schoolYearId) return prev;
      return { ...prev, schoolYearId: currentSchoolYearId };
    });
  }, [currentSchoolYearId]);

  // Reset to page 1 and close detail row when tab changes — compare with previous value to skip
  // initial mount (and React 18 StrictMode's double-invoke).
  const prevActiveTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.closeExpandedRow();
    dt.setCurrentPage(1);
  }, [activeTab]);

  const [validateId, setValidateId] = React.useState<string | null>(null);

  const updateFilters = (patch: Partial<Filters>) => {
    dt.setFilters((prev) => ({ ...prev, ...patch }));
    dt.setCurrentPage(1);
  };

  const queryParams = React.useMemo(
    () =>
      buildRemoteReportListParams(dt.filters, {
        page: dt.currentPage,
        pageSize: dt.itemsPerPage,
        activeTab,
        allowedTabIds,
      }),
    [activeTab, allowedTabIds, dt.filters, dt.currentPage, dt.itemsPerPage],
  );

  const isHiddenByWrapper = React.useContext(CollapseReplaceHiddenContext);
  /** All-tab list waits until RBAC `allowedTabIds` is resolved. */
  const permissionsReady = !isAllReportsTab(activeTab) || allowedTabIds !== undefined;
  const {
    data: apiResponse,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['reports-remote', 'list', queryParams],
    queryFn: () => listReports(queryParams),
    enabled: permissionsReady && !isHiddenByWrapper,
  });

  const reports = apiResponse?.data || [];
  const totalItems = apiResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = Math.min(startIndex + dt.itemsPerPage, totalItems);

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = !isLoading && totalItems === 0;

  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning('Aucun compte rendu à exporter avec les filtres actuels.');
      return;
    }
    setIsExporting(true);
    try {
      const { page: _page, pageSize: _pageSize, ...exportFilters } = queryParams;
      await exportReports(exportFilters);
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [isEmptyForExport, queryParams]);

  const validateMutation = useMutation({
    mutationFn: (id: string) => validateReport(id),
    onSuccess: () => {
      toast.success('Compte rendu validé');
      void queryClient.invalidateQueries({ queryKey: ['reports-remote'] });
      setValidateId(null);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const hasActiveFilters = React.useMemo(
    () => hasRemoteReportActiveFilters(dt.filters),
    [dt.filters],
  );

  const toggleStatusFilter = (status: string) => {
    updateFilters({ reportStatus: toggleListItem(dt.filters.reportStatus, status) });
  };

  const toggleScoringFilter = (scoring: string) => {
    updateFilters({ scoringStatus: toggleListItem(dt.filters.scoringStatus, scoring) });
  };

  const setDateRange = (range: Filters['dateRange']) => {
    updateFilters({ dateRange: range, customDateFrom: undefined, customDateTo: undefined });
  };

  const setCustomDateRange = (from: Date | undefined, to: Date | undefined) => {
    if (from || to) {
      updateFilters({ customDateFrom: from, customDateTo: to, dateRange: 'all' });
    } else {
      updateFilters({ customDateFrom: undefined, customDateTo: undefined });
    }
  };

  const setSearch = (value: string | undefined) => updateFilters({ search: value });
  const setTutorFilter = (id: string | undefined) => updateFilters({ tutorUserId: id });
  const setStudentFilter = (id: string | undefined) => updateFilters({ studentUserId: id });
  const setProgramFilter = (id: string | undefined) => updateFilters({ programId: id });
  const setSchoolYearFilter = (id: string | undefined) => updateFilters({ schoolYearId: id });
  const setManagedByFilter = (value: ManagedByValue) => updateFilters({ managedByIds: value });

  /** PATCH the report status to validated. */
  const handleValidate = (reportId: string) => {
    if (onValidate) {
      onValidate(reportId);
    } else {
      setValidateId(reportId);
    }
  };

  const filtersGroupContent = React.useMemo(
    () => (
      <ReportFilters
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onToggleScoring={toggleScoringFilter}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetSearch={setSearch}
        onSetTutorFilter={setTutorFilter}
        onSetStudentFilter={setStudentFilter}
        onSetManagedByFilter={setManagedByFilter}
        onSetProgramFilter={setProgramFilter}
        onSetSchoolYearFilter={setSchoolYearFilter}
        activeTab={activeTab}
      />
    ),
    [dt.filters, dt.allowedFilterIds, dt.visibleFilterIds, activeTab],
  );

  const filterGroups: FilterGroup[] = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom',
        customContent: filtersGroupContent,
      },
    ],
    [filtersGroupContent],
  );

  const toolbarActions =
    enableFilters || showExport ? (
      <>
        {enableFilters && (
          <Button
            preset="toolbar-filter"
            className={cn(dt.showFilters && 'section-action-button-active')}
            showBadge={hasActiveFilters}
            onClick={() => {
              if (!dt.showFilters) dt.setShowConfigure(false);
              dt.setShowFilters((prev) => !prev);
            }}
          />
        )}
        {showExport && (
          <Button
            preset="toolbar-export"
            tooltipText={
              isEmptyForExport && !isExporting
                ? 'Aucun compte rendu à exporter avec les filtres actuels.'
                : 'Exporter la liste des comptes rendus en Excel'
            }
            onClick={() => void handleExportExcel()}
            loading={isExporting}
            disabled={isEmptyForExport}
          />
        )}
      </>
    ) : undefined;

  const filtersPanel = enableFilters ? (
    <DataTableFilters
      show={dt.showFilters}
      hasActiveFilters={hasActiveFilters}
      filterGroups={filterGroups}
      onClearFilters={dt.clearFilters}
      resultCount={totalItems}
      resultLabel="compte(s) rendu(s)"
      onToggleConfigure={
        dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
      }
      showConfigure={dt.showConfigure}
      configureContent={
        <div className="flex flex-wrap gap-3">
          {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
            const isOn = dt.filterVisibility[id] ?? true;
            return (
              <Button
                key={id}
                variant="custom"
                size="sm"
                onClick={() => dt.handleToggleFilterVisibility(id)}
                disabled={dt.isSavingFilterVisibility}
                className={cn(
                  dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                  isOn ? 'filter-option-primary--active' : 'filter-option-primary',
                )}
              >
                {FILTER_GROUP_LABELS[id]}
              </Button>
            );
          })}
        </div>
      }
    />
  ) : undefined;

  const renderTableHead = () => {
    const headers = reportTableHeaders(activeTab);
    return (
      <tr className="data-table-header-row">
        {headers.map((h) => (
          <th key={h} className="data-table-header-cell">
            {h}
          </th>
        ))}
      </tr>
    );
  };

  const renderTableRow = (report: any) => (
    <ReportRow
      key={report.id}
      report={report}
      activeTab={activeTab}
      onEdit={() => onEditReport?.(report.id)}
      onView={() => onToggleRow?.(report.id)}
      onValidate={() => handleValidate(report.id)}
      pageActions={pageActions}
    />
  );

  const renderCard = (report: any) => (
    <ReportCard
      key={report.id}
      report={report}
      activeTab={activeTab}
      onEdit={() => onEditReport?.(report.id)}
      onView={() => onToggleRow?.(report.id)}
      onValidate={() => handleValidate(report.id)}
      pageActions={pageActions}
    />
  );

  const expandedColSpan = reportTableExpandedColSpan(activeTab);
  const {
    data: expandedReportData,
    isLoading: isLoadingExpandedReport,
    isError: isExpandedReportError,
    error: expandedReportError,
  } = useGetReport(dt.expandedRowId ?? '', { enabled: !!dt.expandedRowId });
  useUnitGetNotFound({
    isError: isExpandedReportError,
    error: expandedReportError,
    catalogKey: 'reportNotFound',
    onClose: onCloseDetails,
    entityId: dt.expandedRowId,
  });
  const expandedItem = (expandedReportData ?? null) as unknown as ReportListItem | null;

  const renderReportDetails = (report: ReportListItem) =>
    renderDetailsContent?.(report) ?? <ReportDetails report={report as unknown as ReportDetail} />;

  function handleRetry(): void {
    void refetch();
  }

  const listErrorText = isError ? getErrorMessage('loadReportsFailed') : undefined;

  return (
    <DataTable
      data={reports}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="comptes rendus"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={expandedColSpan}
      expandedRowId={dt.expandedRowId}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedReport}
      renderExpandedRow={renderReportDetails}
      renderDetailsContent={renderReportDetails}
      onCloseDetails={onCloseDetails}
      toolbarActions={toolbarActions}
      filtersPanel={filtersPanel}
      renderTableHead={renderTableHead}
      renderTableRow={renderTableRow}
      renderCard={renderCard}
      modals={
        <ValidateModal
          open={!!validateId}
          onClose={() => setValidateId(null)}
          title="Valider le compte rendu"
          description="Êtes-vous sûr de vouloir valider ce compte rendu ?"
          isLoading={validateMutation.isPending}
          onConfirm={() => {
            if (validateId) validateMutation.mutate(validateId);
          }}
        />
      }
      emptyIcon={FileText}
      emptyText="Aucun compte rendu trouvé"
      errorText={listErrorText}
      onRetry={handleRetry}
      isSection={isSection}
      title="Comptes rendus"
      sidebarMenuId="menu-reports"
      toolbarLeft={toolbarLeft}
    />
  );
};
